import * as R from 'ramda';
import { createSelector } from 'reselect';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// feature load-details-new
import * as H from '../helpers';
//////////////////////////////////////////////////

const selectLoadDetailsStore = (state) => state.loadDetails;

const makeSelectEvents = () => createSelector(
  selectLoadDetailsStore,
  ({ events }) => R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX), R.values(events)),
);

const makeSelectLoadDetails = () => createSelector(
  selectLoadDetailsStore,
  ({ details }) => details,
);

const makeSelectDispatchPlannerOpened = () => createSelector(
  selectLoadDetailsStore,
  ({ dispatchPlannerOpened }) => dispatchPlannerOpened,
);

const makeSelectDispatchPlannerEventsOpened = () => createSelector(
  selectLoadDetailsStore,
  ({ dispatchPlannerEventsOpened }) => dispatchPlannerEventsOpened,
);

const makeSelectLists = () => createSelector(
  selectLoadDetailsStore,
  ({ lists }) => H.getListsWithFleetCarrierRateLists(lists),
);

const makeSelectLoadGuid = () => createSelector(
  selectLoadDetailsStore,
  ({ details }) => R.path([GC.FIELD_GUID], details),
);

const makeSelectLoadConfigs = () => createSelector(
  selectLoadDetailsStore,
  ({ loadConfigs }) => loadConfigs,
);

const makeSelectCharges = () => createSelector(
  selectLoadDetailsStore,
  ({ charges }) => charges,
);

const makeSelectSelectedCustomerRate = () => createSelector(
  selectLoadDetailsStore,
  R.compose(
    R.find(R.pathEq(true, ['selected'])),
    R.pathOr([], ['lists', 'customerRateList']),
  ),
);

const makeSelectSelectedCarrierDriverRate = () => createSelector(
  selectLoadDetailsStore,
  R.compose(
    R.find(R.pathEq(true, ['selected'])),
    R.pathOr([], ['lists', 'driverCarrierRateList']),
  ),
);

const makeSelectHasLegRates = () => createSelector(
  selectLoadDetailsStore,
  R.compose(
    G.isNotNilAndNotEmpty,
    R.path(['lists', 'legRateList']),
  ),
);

const makeSelectSelectedCarrierDriverRateGuid = () => createSelector(
  selectLoadDetailsStore,
  R.compose(
    G.getGuidFromObject,
    R.find(R.pathEq(true, ['selected'])),
    R.pathOr([], ['lists', 'driverCarrierRateList']),
  ),
);

const makeSelectActiveListNames = () => createSelector(
  selectLoadDetailsStore,
  ({ activeLists }) => activeLists,
);

const makeSelectActiveLists = () => createSelector(
  selectLoadDetailsStore,
  ({ lists, activeLists }) => {
    const listsWithRate = H.getListsWithFleetCarrierRateLists(lists);

    return R.compose(
      R.indexBy(R.prop('listName')),
      R.map((listName) => ({
        listName,
        pagination: R.path([listName, 'pagination'], listsWithRate),
        totalCount: R.path([listName, 'totalCount'], listsWithRate),
        list: R.or(R.path([listName, 'list'], listsWithRate), R.path([listName], listsWithRate)),
      })),
      R.uniq,
    )(activeLists);
  },
);

const makeSelectLoadTotalWeight = () => createSelector(
  selectLoadDetailsStore,
  ({ details }) => {
    const items = R.pathOr([], [GC.FIELD_LOAD_ITEMS], details);
    if (G.isNotNilAndNotEmpty(items)) {
      const total = G.calcItemsTotalWeightWithoutQty(items);

      return G.renameKeys({
        [GC.FIELD_ITEM_WEIGHT]: GC.FIELD_TOTAL_TRIP_WEIGHT,
        [GC.FIELD_ITEM_WEIGHT_TYPE]: GC.FIELD_TOTAL_TRIP_WEIGHT_UOM,
      }, total);
    }

    return {};
  },
);

const makeSelectLoadTotalDistance = () => createSelector(
  selectLoadDetailsStore,
  ({ details }) => {
    const distances = R.compose(
      R.filter(G.isNotNilAndNotEmpty),
      R.map(R.prop(GC.FIELD_DISTANCE_TEL)),
      R.pathOr([], [GC.FIELD_LOAD_STOPS]),
    )(details);

    return G.calculateTotalDistance(distances);
  },
);

const makeSelectLoadDocuments = () => createSelector(
  selectLoadDetailsStore,
  ({ documents }) => documents,
);

const makeSelectLinkedOrdersDocuments = () => createSelector(
  selectLoadDetailsStore,
  ({ linkedOrdersDocuments }) => linkedOrdersDocuments,
);

const makeSelectDeclineReasonOptions = () => createSelector(
  selectLoadDetailsStore,
  ({ loadConfigs }) => R.compose(
    R.map((item) => ({
      [GC.FIELD_VALUE]: G.getParentGuidOrGuidFromObject(item),
      [GC.FIELD_LABEL]: R.prop(GC.FIELD_DISPLAYED_VALUE, item),
    })),
    R.pathOr(
      [],
      [
        'configGroups',
        GC.COMMUNICATION_CONFIG_GROUP,
        'dropdowns',
        GC.COMMUNICATION_DISPATCH_DECLINE_REASON_CODE,
        'options',
      ],
    ),
  )(loadConfigs),
);

const makeSelectStatusMessageConfigs = () => createSelector(
  selectLoadDetailsStore,
  ({ loadConfigs: { statusMessageConfigs } }) => statusMessageConfigs,
);

const makeSelectMessageCenter = () => createSelector(
  selectLoadDetailsStore,
  ({ messageCenter }) => messageCenter,
);

const makeSelectMessageCenterList = () => createSelector(
  selectLoadDetailsStore,
  ({ messageCenter }) => {
    const { activeTab } = messageCenter;
    const entities = R.pathOr([], [activeTab], messageCenter);

    return { entities, activeTab };
  },
);

const makeSelectNotification = () => createSelector(
  selectLoadDetailsStore,
  ({ notification }) => notification,
);

const makeSelectPinnedNote = () => createSelector(
  selectLoadDetailsStore,
  ({ messageCenter }) => R.compose(
    R.find(R.propEq(true, 'pinned')),
    R.prop('noteList'),
  )(messageCenter),
);

const makeSelectMailList = () => createSelector(
  selectLoadDetailsStore,
  ({ mailList }) => mailList,
);

const makeSelectRelatedOrderList = () => createSelector(
  selectLoadDetailsStore,
  ({ relatedOrderList }) => relatedOrderList,
);

const makeSelectRelatedOrderListLoading = () => createSelector(
  selectLoadDetailsStore,
  ({ relatedOrderListLoading }) => relatedOrderListLoading,
);

const makeSelectLinkedOrderList = () => createSelector(
  selectLoadDetailsStore,
  ({ linkedOrderList }) => linkedOrderList,
);

const makeSelectCloEventReferences = () => createSelector(
  selectLoadDetailsStore,
  ({ cloEventReferences }) => cloEventReferences,
);

const makeSelectExpandedLists = () => createSelector(
  selectLoadDetailsStore,
  ({ expandedLists }) => expandedLists,
);

const makeSelectHiddenTabs = () => createSelector(
  selectLoadDetailsStore,
  ({ hiddenTabs }) => hiddenTabs,
);

const makeSelectTelDocumentTemplates = () => createSelector(
  selectLoadDetailsStore,
  ({ documentTemplates }) => documentTemplates,
);

const makeSelectLoadInvoiceTotals = () => createSelector(
  selectLoadDetailsStore,
  ({ invoiceTotals }) => invoiceTotals,
);

const makeSelectLoadCustomStatuses = () => createSelector(
  selectLoadDetailsStore,
  ({ loadCustomStatuses }) => loadCustomStatuses,
);

const makeSelectLoadTransportationModeGroupingList = () => createSelector(
  selectLoadDetailsStore,
  ({ transportationModeGroupingList }) => transportationModeGroupingList,
);

const makeSelectPostedShipmentsData = () => createSelector(
  selectLoadDetailsStore,
  ({ postedShipmentsData }) => postedShipmentsData,
);

const makeSelectClaimGeneralDetails = () => createSelector(
  selectLoadDetailsStore,
  ({ claimGeneralDetails }) => (
    R.equals(R.length(claimGeneralDetails), 1) ? R.head(claimGeneralDetails) : claimGeneralDetails
  ),
);

const makeSelectIsAnyEnabledCrossBorder = () => createSelector(
  selectLoadDetailsStore,
  ({ isAnyEnabledCrossBorder }) => isAnyEnabledCrossBorder,
);

const makeSelectCarrierInvoiceList = () => createSelector(
  selectLoadDetailsStore,
  R.pathOr([], ['lists', 'carrierInvoiceList']),
);

const makeSelectDriverInvoiceList = () => createSelector(
  selectLoadDetailsStore,
  R.pathOr([], ['lists', 'driverInvoiceList']),
);

const makeSelectLoadNoteList = () => createSelector(
  selectLoadDetailsStore,
  R.pathOr([], ['messageCenter', 'noteList']),
);

const makeSelectPreselectedTrailers = () => createSelector(
  selectLoadDetailsStore,
  ({ preselectedTrailers }) => preselectedTrailers,
);

const makeSelectTrailerOptions = () => createSelector(
  selectLoadDetailsStore,
  ({ trailerOptions }) => trailerOptions,
);

const makeSelectCubicCapacityWarning = createSelector(
  [
    R.pathOr([], ['loadDetails', 'details', GC.FIELD_LOAD_ITEMS]),
    R.path(['loadDetails', 'loadConfigs', 'configGroups', GC.GENERAL_CONFIG_GROUP]),
    R.path(['loadDetails', 'details', GC.SYSTEM_LIST_CLOS]),
  ],
  (items, configs, clos) => {
    if (R.equals(R.length(clos), 1)) return H.getCubicCapacityWarningErrors(items, configs);

    const config = G.getConfigValueFromStore(GC.GENERAL_UOM_CALC_DEFAULT_UOM_SYSTEM, configs);
    const isImperial = R.equals(config, GC.IMPERIAL_SYSTEM);

    const warningLocale = G.getWindowLocale(
      'titles:cubic-capacity-warning',
      'This shipment exceeds recommended cubic capacity guidelines.',
    );

    const totalVolumeLocale = G.getWindowLocale(...G.ifElse(
      isImperial,
      ['titles:total-cubic-feet', 'Total Cubic Feet'],
      ['titles:total-cubic-metres', 'Total Cubic Metres'],
    ));

    const groupedItems = R.groupBy(R.prop(GC.FIELD_LOAD_GUID), items);

    const warnings = R.compose(
      R.append(warningLocale),
      R.filter(R.isNotNil),
      R.map(({ guid, primaryReference: { value } = {} }) => {
        const cloItems = R.prop(guid, groupedItems);

        const {
          totalDensity,
          totalCubicVolume,
        } = G.getTotalCubicCapacityValuesFromEvents({ config, items: [cloItems] });

        const showWarning = G.checkCubicCapacityRules({ config, totalDensity, totalCubicVolume });

        return showWarning ? `${value}: ${totalVolumeLocale} - ${totalCubicVolume}, ${
          G.getWindowLocale('titles:average-density', 'Average Density')} - ${totalDensity}` : null;
      }),
    )(clos);

    return warnings;
  },
);

export {
  makeSelectLists,
  makeSelectEvents,
  makeSelectCharges,
  makeSelectLoadGuid,
  makeSelectMailList,
  makeSelectPinnedNote,
  makeSelectHiddenTabs,
  makeSelectLoadDetails,
  makeSelectHasLegRates,
  makeSelectLoadConfigs,
  makeSelectActiveLists,
  makeSelectNotification,
  makeSelectLoadNoteList,
  makeSelectExpandedLists,
  makeSelectLoadDocuments,
  makeSelectMessageCenter,
  makeSelectTrailerOptions,
  makeSelectActiveListNames,
  makeSelectLoadTotalWeight,
  makeSelectLinkedOrderList,
  makeSelectRelatedOrderList,
  makeSelectMessageCenterList,
  makeSelectDriverInvoiceList,
  makeSelectLoadInvoiceTotals,
  makeSelectLoadTotalDistance,
  makeSelectCloEventReferences,
  makeSelectCarrierInvoiceList,
  makeSelectLoadCustomStatuses,
  makeSelectPostedShipmentsData,
  makeSelectClaimGeneralDetails,
  makeSelectPreselectedTrailers,
  makeSelectCubicCapacityWarning,
  makeSelectTelDocumentTemplates,
  makeSelectSelectedCustomerRate,
  makeSelectDeclineReasonOptions,
  makeSelectStatusMessageConfigs,
  makeSelectDispatchPlannerOpened,
  makeSelectLinkedOrdersDocuments,
  makeSelectIsAnyEnabledCrossBorder,
  makeSelectRelatedOrderListLoading,
  makeSelectSelectedCarrierDriverRate,
  makeSelectDispatchPlannerEventsOpened,
  makeSelectSelectedCarrierDriverRateGuid,
  makeSelectLoadTransportationModeGroupingList,
};
